/**
 * Upload Page - Upload videos with comprehensive metadata
 */

import { createNavigation } from '../components/navigation.js';
import { api } from '../lib/api.js';
import {
  createUploadController,
  loadUploadRecord,
  normalizeUploadStatus,
  removeUploadRecord,
  saveUploadRecord,
  UploadError,
  uploadAssetsFile,
} from '../lib/assets-upload.js';
import { getLanguage, t } from '../lib/i18n.js';
import { refreshToken } from '../lib/keycloak.js';
import { filterLanguages, SUBTITLE_LANGUAGES } from '../lib/languages.js';
import { showConfirm, showModal } from '../lib/modal.js';
import { RIGHTS_TERMS_VERSION } from '../lib/rightsTerms.js';
import { router } from '../lib/router.js';

export async function renderUpload() {
  const app = document.getElementById('app');
  app.innerHTML = '';

  app.appendChild(await createNavigation());

  const main = document.createElement('main');
  main.className = 'admin-main';
  main.innerHTML = `
    <div class="container">
      <div class="mb-lg">
        <h1>${t('upload_title')}</h1>
      </div>
      
      <!-- Guide Section -->
      <div class="card" style="max-width: 900px; margin: 0 auto; margin-bottom: var(--spacing-lg); background-color: rgba(0, 119, 192, 0.1); border: 1px solid rgba(0, 119, 192, 0.3);">
        <div class="card-header" style="cursor: pointer;" onclick="const guideContent = this.parentElement.querySelector('.guide-content'); const isHidden = guideContent.style.display === 'none'; guideContent.style.display = isHidden ? 'block' : 'none'; this.querySelector('.guide-toggle').textContent = isHidden ? '▲' : '▼'; this.querySelector('.guide-hint').style.display = isHidden ? 'none' : 'block';">
          <h2 class="card-title" style="margin: 0; display: flex; justify-content: space-between; align-items: center;">
            <span>Vegleiðing til stroyma.fo</span>
            <span class="guide-toggle" style="font-size: 1.2rem;">▼</span>
          </h2>
          <p class="guide-hint" style="margin: var(--spacing-sm) 0 0 0; color: var(--color-cloud-grey); font-size: 0.875rem; font-style: italic;">Trýst her fyri at síggja vegleiðingina</p>
        </div>
        <div class="guide-content" style="display: none; padding: var(--spacing-md);">
          <div style="color: var(--color-mist-grey); line-height: 1.8;">
            <p style="margin-bottom: var(--spacing-md);"><strong>Vælkomin til Stroyma.</strong> Tú ert nú í ferð við at leggja filmin hjá tær út. Fylg mannagongdini, sum er lýst niðanfyri. Teigar, ið hava eina stjørnu (*), eru kravdir teigar.</p>

            <p style="margin-bottom: var(--spacing-md);">Far inn á stroyma.fo og rulla oman til niðast á síðuni. Í høgra horni sært tú funkuna "Rita inn/Skráset brúkara". Trýst á hana og ger ein brúkara. Lættast er at gera brúkara við Google ella Facebook. Tá tú ert ritað/ur inn, skalt tú trýsta á "Upload New Video".</p>

            <p style="margin-bottom: var(--spacing-sm);"><strong>Filmurin skal leggjast út í HD, FHD ella QHD (2K).</strong> Set teg í samband við Filmshúsið, um tú vilt leggja út í 4K.</p>

            <p style="margin-bottom: var(--spacing-sm);"><strong>Vel ein flokk</strong></p>
            <ul style="margin-left: var(--spacing-lg); margin-bottom: var(--spacing-md);">
              <li style="margin-bottom: var(--spacing-xs);">Stuttfilmur er ein fiktiónsfilmur á 30 min. ella styttri.</li>
              <li style="margin-bottom: var(--spacing-xs);">Animatión og listafilmar verða flokkaðir sum stuttfilmar (um hesir eru styttri enn 30 min.)</li>
              <li style="margin-bottom: var(--spacing-xs);">Fiktiónsfilmar, sum eru longri enn 30 min., verða flokkaðir sum spælifilmar.</li>
              <li style="margin-bottom: var(--spacing-xs);">Stuttur heimildarfilmur er 30 min. ella styttri.</li>
              <li style="margin-bottom: var(--spacing-xs);">Longri heimildarfilmur er longri enn 30 min.</li>
            </ul>

            <p style="margin-bottom: var(--spacing-sm);"><strong>Logline og synopsa</strong></p>
            <p style="margin-bottom: var(--spacing-xs);">"Stuttur søgusamandráttur" er ein logline við í mesta lagi 200 teknum.</p>
            <p style="margin-bottom: var(--spacing-sm);">"Um filmin" er ein synopsa við í mesta lagi 600 teknum.</p>
            <p style="margin-bottom: var(--spacing-md);">Gevið gætur, at hesi skulu skrivast á bæði føroyskum og enskum.</p>

            <p style="margin-bottom: var(--spacing-sm);"><strong>Myndir</strong></p>
            <ul style="margin-left: var(--spacing-lg); margin-bottom: var(--spacing-md);">
              <li style="margin-bottom: var(--spacing-xs);">Plakat er forsíðumyndin, ein portrettmynd, sum er standandi.</li>
              <li style="margin-bottom: var(--spacing-xs);">Stillmyndin er ein liggjandi mynd í 16:9.</li>
              <li style="margin-bottom: var(--spacing-xs);">Støddarmarkið á fíluni er 50 MB og loyvdu formatini eru JPEG (.jpg og .jpeg), PNG (.png) og WebP (.webp).</li>
            </ul>

            <p style="margin-bottom: var(--spacing-sm);"><strong>Undirtekstir</strong></p>
            <p style="margin-bottom: var(--spacing-xs);">Til ber at leggja upp undirtekstir á fleiri málum. Legg upp .srt fíluna og vel mál. Trýst á "+ Add subtitle" fyri at leggja fleiri aftrat.</p>
            <p style="margin-bottom: var(--spacing-md);">Í teiginum undir skal tú skriva hvørji mál undirtekstirnir eru t.d. "Føroyskar, enskar, danskar". Eru eingir, noyðist tú ikki at skriva nakað í teigin.</p>

            <p style="margin-bottom: var(--spacing-sm);"><strong>Leinki til IMDb</strong></p>
            <p style="margin-bottom: var(--spacing-md);">Hetta er til IMDb vangan hjá filminum.</p>

            <p style="margin-bottom: var(--spacing-sm);"><strong>Kostnaðurin</strong></p>
            <p style="margin-bottom: var(--spacing-sm);">Kostnaðurin á filminum er mettur út frá, hvussu gamal filmurin er. Framleiðarin/ábyrgdarfólkið kann til eina og hvørja tíð avgera, um filmurin skal vera ókeypis.</p>
            <p style="margin-bottom: var(--spacing-md);">Felagið Føroysk Filmsfólk og stýrið fyri Filmshúsið hava í felag avgjørt ein vegleiðandi kostnað. Til ber at áseta ein serstakan kostnað, um tað er neyðugt. Prísurin er íroknaður mvg og harumframt fer ein partur av kostnaðinum til at hýsa skipanini og til tænastuveiting.</p>

            <p style="margin-bottom: var(--spacing-sm);"><strong>${t('rights_warranty_title')}</strong></p>
            <p style="margin-bottom: var(--spacing-sm);">Niðast á síðuni skalt tú vátta, at tú hevur rættindini til at leggja filmin út, og at tú hevur lisið og góðtikið treytirnar fyri rættindahavarar.</p>
          </div>
        </div>
      </div>
      
      <div class="card" style="max-width: 900px; margin: 0 auto;">
        <form id="upload-form">
          <!-- File Upload Zone -->
          <div class="form-section">
            <h3 class="form-section-title">Videofíla</h3>
            <div class="form-group">
              <div class="upload-zone" id="upload-zone">
                <div class="upload-icon">📹</div>
                <div class="upload-text">${t('upload_drag_drop')}</div>
                <input type="file" id="file-input" accept="video/*,.mxf,.MXF" style="display: none;">
              </div>
              <div id="file-info" style="display: none; margin-top: 16px;">
                <div class="flex flex-between flex-center" style="padding: 12px; background: rgba(0, 119, 192, 0.1); border-radius: 8px;">
                  <div>
                    <strong id="file-name"></strong>
                    <div style="font-size: 14px; color: var(--color-cloud-grey);"><span id="file-size"></span> <span id="file-format" style="margin-left: 8px; opacity: 0.8;"></span></div>
                  </div>
                  <button type="button" class="btn btn-small btn-danger" id="remove-file">${t('upload_remove')}</button>
                </div>
              </div>
            </div>
            
            <!-- Progress Bar -->
            <div id="upload-progress" style="display: none;">
              <div class="form-label">${t('upload_uploading')}</div>
              <div class="progress-bar">
                <div class="progress-fill" id="progress-fill" style="width: 0%"></div>
              </div>
              <div style="text-align: center; margin-top: 8px; color: var(--color-cloud-grey);">
                <span id="progress-text">0%</span>
              </div>
            </div>
          </div>
          
          <!-- Category -->
          <div class="form-section">
            <h3 class="form-section-title">${t('form_category')} *</h3>
            <div class="form-group">
              <select id="category" class="form-input" required>
                <option value="">${t('form_category')}...</option>
                <option value="short">${t('category_short')}</option>
                <option value="feature">${t('category_feature')}</option>
                <option value="documentary_short">${t('category_documentary_short')}</option>
                <option value="documentary_long">${t('category_documentary_long')}</option>
              </select>
            </div>
          </div>

          <!-- Age Rating -->
          <div class="form-section">
            <h3 class="form-section-title">Aldursmark</h3>
            <div class="form-group">
              <select id="ageRating" class="form-input">
                <option value="">Einki valt</option>
                <option value="A">A (Allir)</option>
                <option value="7">7</option>
                <option value="11">11</option>
                <option value="15">15</option>
              </select>
              <p class="form-help">Danskt/nordiskt aldursmarkering (Medierådet)</p>
            </div>
          </div>
          
          <!-- Basic Information -->
          <div class="form-section">
            <h3 class="form-section-title">${t('form_section_basic')}</h3>
            
            <div class="form-group">
              <label class="form-label" for="originalTitle">${t('form_original_title')} *</label>
              <input type="text" id="originalTitle" class="form-input" required>
            </div>
            
            <div class="form-group">
              <label class="form-label" for="internationalTitle">${t('form_international_title')}</label>
              <input type="text" id="internationalTitle" class="form-input">
            </div>
          </div>
          
          <!-- Faroese Information -->
          <div class="form-section">
            <h3 class="form-section-title">🇫🇴 FØROYSKT / FAROESE</h3>
            
            <div class="form-group">
              <label class="form-label" for="loglineFo">Stuttur søgusamandráttur * <small style="color: var(--color-cloud-grey);">(í mesta lagi 200 tekin)</small></label>
              <textarea id="loglineFo" class="form-textarea" rows="2" maxlength="200" placeholder="Stuttur søgusamandráttur..."></textarea>
              <small style="color: var(--color-cloud-grey); font-size: 0.875rem;"><span id="loglineFo-count">0</span>/200 tekin</small>
            </div>
            
            <div class="form-group">
              <label class="form-label" for="synopsisFo">Um filmin * <small style="color: var(--color-cloud-grey);">(í mesta lagi 600 tekin)</small></label>
              <textarea id="synopsisFo" class="form-textarea" rows="4" maxlength="600" placeholder="Um filmin..."></textarea>
              <small style="color: var(--color-cloud-grey); font-size: 0.875rem;"><span id="synopsisFo-count">0</span>/600 tekin</small>
            </div>
          </div>
          
          <!-- English Information -->
          <div class="form-section">
            <h3 class="form-section-title">🇬🇧 ENSKT / ENGLISH</h3>
            
            <div class="form-group">
              <label class="form-label" for="loglineEn">Logline * <small style="color: var(--color-cloud-grey);">(max. 200 characters)</small></label>
              <textarea id="loglineEn" class="form-textarea" rows="2" maxlength="200" placeholder="Logline..."></textarea>
              <small style="color: var(--color-cloud-grey); font-size: 0.875rem;"><span id="loglineEn-count">0</span>/200 tekin</small>
            </div>
            
            <div class="form-group">
              <label class="form-label" for="synopsisEn">Synopsis * <small style="color: var(--color-cloud-grey);">(max. 600 characters)</small></label>
              <textarea id="synopsisEn" class="form-textarea" rows="4" maxlength="600" placeholder="Synopsis..."></textarea>
              <small style="color: var(--color-cloud-grey); font-size: 0.875rem;"><span id="synopsisEn-count">0</span>/600 tekin</small>
            </div>
          </div>
          
          <!-- Basic Information (continued) -->
          <div class="form-section">
            <h3 class="form-section-title">${t('form_section_basic')}</h3>
            
            <!-- Poster Image -->
            <div class="form-group">
              <label class="form-label">${t('form_poster_url')}</label>
              <div style="display: flex; gap: 12px; flex-direction: column;">
                <div style="display: flex; gap: 12px; align-items: center;">
                  <input type="file" id="posterFile" accept="image/jpeg,image/png,image/webp" style="display: none;">
                  <button type="button" class="btn btn-secondary" id="selectPosterBtn">📁 ${t('upload_select_file')}</button>
                  <span id="posterFileName" style="color: var(--color-cloud-grey); font-size: 0.875rem;"></span>
                </div>
                <div style="text-align: center; color: var(--color-cloud-grey); font-size: 0.875rem;">ella</div>
                <input type="url" id="posterUrl" class="form-input" placeholder="${t('placeholder_poster_url')}">
              </div>
              <p class="form-help">Legg upp fílu ella set inn URL • Tilmælt: 640x960px (2:3 standandi)</p>
            </div>
            
            <!-- Still Image -->
            <div class="form-group">
              <label class="form-label">${t('form_still_url')}</label>
              <div style="display: flex; gap: 12px; flex-direction: column;">
                <div style="display: flex; gap: 12px; align-items: center;">
                  <input type="file" id="stillFile" accept="image/jpeg,image/png,image/webp" style="display: none;">
                  <button type="button" class="btn btn-secondary" id="selectStillBtn">📁 ${t('upload_select_file')}</button>
                  <span id="stillFileName" style="color: var(--color-cloud-grey); font-size: 0.875rem;"></span>
                </div>
                <div style="text-align: center; color: var(--color-cloud-grey); font-size: 0.875rem;">ella</div>
                <input type="url" id="stillUrl" class="form-input" placeholder="${t('placeholder_still_url')}">
              </div>
              <p class="form-help">Legg upp fílu ella set inn URL • Tilmælt: 1920x1080px (16:9 liggjandi)</p>
            </div>
          </div>
          
          <!-- Undirtekstir -->
          <div class="form-section">
            <h3 class="form-section-title">Undirtekstir</h3>
            <div id="subtitle-list" style="margin-bottom: var(--spacing-md);"></div>
            <button type="button" class="btn btn-secondary" id="add-subtitle-btn">+ Legg afturat undirtekstir</button>

            <div class="form-group" style="margin-top: var(--spacing-md);">
              <label class="form-label" for="subtitles">${t('form_subtitles')}</label>
              <input type="text" id="subtitles" class="form-input" placeholder="Føroyskt, Enskt, etc.">
            </div>
          </div>
          
          <!-- Crew -->
          <div class="form-section">
            <h3 class="form-section-title">${t('form_section_crew')}</h3>
            
            <!-- Director -->
            <div class="form-group">
              <label class="form-label">${t('form_director')}</label>
              <div id="director-container">
                <div class="name-input-group">
                  <input type="text" id="director0FirstName" class="form-input" placeholder="${t('placeholder_firstname')}">
                  <input type="text" id="director0MiddleName" class="form-input" placeholder="${t('placeholder_middlename')}">
                  <input type="text" id="director0LastName" class="form-input" placeholder="${t('placeholder_lastname')}">
                </div>
              </div>
              <button type="button" class="btn btn-secondary btn-small mt-sm add-role-btn" data-role="director">+ Legg aftrat</button>
            </div>

            <!-- Screenwriter -->
            <div class="form-group">
              <label class="form-label">${t('form_screenwriter')}</label>
              <div id="screenwriter-container">
                <div class="name-input-group">
                  <input type="text" id="screenwriter0FirstName" class="form-input" placeholder="${t('placeholder_firstname')}">
                  <input type="text" id="screenwriter0MiddleName" class="form-input" placeholder="${t('placeholder_middlename')}">
                  <input type="text" id="screenwriter0LastName" class="form-input" placeholder="${t('placeholder_lastname')}">
                </div>
              </div>
              <button type="button" class="btn btn-secondary btn-small mt-sm add-role-btn" data-role="screenwriter">+ Legg aftrat</button>
            </div>

            <!-- Producer -->
            <div class="form-group">
              <label class="form-label">${t('form_producer')}</label>
              <div id="producer-container">
                <div class="name-input-group">
                  <input type="text" id="producer0FirstName" class="form-input" placeholder="${t('placeholder_firstname')}">
                  <input type="text" id="producer0MiddleName" class="form-input" placeholder="${t('placeholder_middlename')}">
                  <input type="text" id="producer0LastName" class="form-input" placeholder="${t('placeholder_lastname')}">
                </div>
              </div>
              <button type="button" class="btn btn-secondary btn-small mt-sm add-role-btn" data-role="producer">+ Legg aftrat</button>
            </div>

            <!-- Director of Photography -->
            <div class="form-group">
              <label class="form-label">${t('form_dop')}</label>
              <div id="dop-container">
                <div class="name-input-group">
                  <input type="text" id="dop0FirstName" class="form-input" placeholder="${t('placeholder_firstname')}">
                  <input type="text" id="dop0MiddleName" class="form-input" placeholder="${t('placeholder_middlename')}">
                  <input type="text" id="dop0LastName" class="form-input" placeholder="${t('placeholder_lastname')}">
                </div>
              </div>
              <button type="button" class="btn btn-secondary btn-small mt-sm add-role-btn" data-role="dop">+ Legg aftrat</button>
            </div>

            <!-- Editor -->
            <div class="form-group">
              <label class="form-label">${t('form_editor')}</label>
              <div id="editor-container">
                <div class="name-input-group">
                  <input type="text" id="editor0FirstName" class="form-input" placeholder="${t('placeholder_firstname')}">
                  <input type="text" id="editor0MiddleName" class="form-input" placeholder="${t('placeholder_middlename')}">
                  <input type="text" id="editor0LastName" class="form-input" placeholder="${t('placeholder_lastname')}">
                </div>
              </div>
              <button type="button" class="btn btn-secondary btn-small mt-sm add-role-btn" data-role="editor">+ Legg aftrat</button>
            </div>

            <!-- Music Composer -->
            <div class="form-group">
              <label class="form-label">${t('form_composer')}</label>
              <div id="composer-container">
                <div class="name-input-group">
                  <input type="text" id="composer0FirstName" class="form-input" placeholder="${t('placeholder_firstname')}">
                  <input type="text" id="composer0MiddleName" class="form-input" placeholder="${t('placeholder_middlename')}">
                  <input type="text" id="composer0LastName" class="form-input" placeholder="${t('placeholder_lastname')}">
                </div>
              </div>
              <button type="button" class="btn btn-secondary btn-small mt-sm add-role-btn" data-role="composer">+ Legg aftrat</button>
            </div>
            
            <!-- Main Cast -->
            <div class="form-group">
              <label class="form-label">${t('form_main_cast')}</label>
              <div id="cast-container">
                <div class="name-input-group">
                  <input type="text" id="cast0FirstName" class="form-input" placeholder="${t('placeholder_firstname')}">
                  <input type="text" id="cast0MiddleName" class="form-input" placeholder="${t('placeholder_middlename')}">
                  <input type="text" id="cast0LastName" class="form-input" placeholder="${t('placeholder_lastname')}">
                </div>
              </div>
              <button type="button" class="btn btn-secondary btn-small mt-sm" id="add-cast">+ Legg aftrat leikara</button>
            </div>
            
            <!-- Key Crew -->
            <div class="form-group">
              <label class="form-label">${t('form_key_crew')}</label>
              <div id="crew-container">
                <div class="name-input-group">
                  <select id="crew0Role" class="form-input" style="min-width: 200px;">
                    <option value="">Vel leiklut...</option>
                    <option value="production_designer">Production Designer / Framleiðslusniðgevi</option>
                    <option value="costume_designer">Costume Designer / Búnasniðgevi</option>
                    <option value="makeup_artist">Makeup Artist / Sminkulistafólk</option>
                    <option value="sound_recordist">Sound Recordist / Ljóðmeistari</option>
                    <option value="sound_designer">Sound Designer / Ljóðsniðgevi</option>
                    <option value="gaffer">Gaffer / Ljósmeistari</option>
                    <option value="key_grip">Key Grip / Høvuðsgrippur</option>
                    <option value="colourist">Colourist / Litviðgeri</option>
                  </select>
                  <input type="text" id="crew0FirstName" class="form-input" placeholder="${t('placeholder_firstname')}">
                  <input type="text" id="crew0MiddleName" class="form-input" placeholder="${t('placeholder_middlename')}">
                  <input type="text" id="crew0LastName" class="form-input" placeholder="${t('placeholder_lastname')}">
                </div>
              </div>
              <button type="button" class="btn btn-secondary btn-small mt-sm" id="add-crew">+ Legg aftrat liðsmann</button>
            </div>
          </div>
          
          <!-- Screenings & Awards -->
          <div class="form-section">
            <h3 class="form-section-title">Sýningar & virðislønir</h3>
            <p class="form-help" style="margin-bottom: var(--spacing-md);">Legg aftrat filmsfestivalar, sýningar og virðislønir (í mesta lagi 6 skrásetingar)</p>
            <div id="screenings-container"></div>
            <button type="button" class="btn btn-secondary btn-small mt-sm" id="add-screening">+ Legg aftrat sýning</button>
          </div>

          <!-- Production Companies -->
          <div class="form-section">
            <h3 class="form-section-title">Framleiðsla</h3>

            <div class="form-group">
              <label class="form-label" for="productionCompany">${t('form_production_company')}</label>
              <input type="text" id="productionCompany" class="form-input" placeholder="${t('placeholder_name')}">
            </div>

            <div class="form-group">
              <label class="form-label" for="coproductionCompany">${t('form_coproduction_company')}</label>
              <input type="text" id="coproductionCompany" class="form-input" placeholder="${t('placeholder_name')}">
            </div>
          </div>
          
          <!-- Technical Details -->
          <div class="form-section">
            <h3 class="form-section-title">${t('form_section_technical')}</h3>
            
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px;">
              <div class="form-group">
                <label class="form-label" for="runtimeHours">${t('form_runtime_hours')}</label>
                <input type="number" id="runtimeHours" class="form-input" min="0" max="10" placeholder="0">
              </div>
              
              <div class="form-group">
                <label class="form-label" for="runtimeMinutes">${t('form_runtime_minutes')}</label>
                <input type="number" id="runtimeMinutes" class="form-input" min="0" max="59" placeholder="0">
              </div>
            </div>
            
            <div class="form-group">
              <label class="form-label" for="language">${t('form_language')}</label>
              <input type="text" id="language" class="form-input" placeholder="Føroyskt, Enskt, etc.">
            </div>
            
            <div class="form-group">
              <label class="form-label" for="productionYear">${t('form_production_year')}</label>
              <input type="number" id="productionYear" class="form-input" min="1900" max="2100" placeholder="2024">
            </div>
            
            <div class="form-group">
              <label class="form-label" for="imdbLink">${t('form_imdb_link')}</label>
              <input type="url" id="imdbLink" class="form-input" placeholder="${t('placeholder_link')}">
            </div>
            
            <div class="form-group" style="display: none;">
              <label class="checkbox-label">
                <input type="checkbox" id="encode4K">
                <span>Encode in 4K (for high-resolution source videos)</span>
              </label>
            </div>
          </div>
          
          <!-- Pricing / Cost -->
          <div class="form-section">
            <h3 class="form-section-title">KOSTNAÐUR *</h3>
            
            <div class="form-group">
              <label class="form-label">Prísseting *</label>
              <select id="pricingType" class="form-input" required>
                <option value="standard">Vanlig prísseting</option>
                <option value="free">Ókeypis</option>
                <option value="custom">Onnur upphædd</option>
              </select>
            </div>
            
            <div class="form-group" id="pricing-tier-group" style="display: none;">
              <label class="form-label">Prísflokkur</label>
              <select id="pricingTier" class="form-input">
                <option value="">Vel prísfokk...</option>
                <!-- Options will be populated dynamically based on category -->
              </select>
            </div>
            
            <div class="form-group" id="custom-price-group" style="display: none;">
              <label class="form-label">Annar prísurin (kr.)</label>
              <input type="number" id="customPrice" class="form-input" min="0" step="0.01" placeholder="0.00">
              <p class="form-help">Skriva prís í kr. (t.d. 29.00)</p>
            </div>
            
            <div class="form-group" id="rental-duration-group" style="display: none;">
              <label class="form-label">Leigunarskeið (tímar)</label>
              <input type="number" id="rentalDuration" class="form-input" min="1" max="168" value="48">
              <small style="color: var(--color-cloud-grey); font-size: 0.875rem;">Hvussu leingi brúkarar kunnu hyggja (standard: 48t)</small>
            </div>
          </div>
          
          <!-- Bank Information -->
          <div class="form-section" id="bank-info-section">
            <h3 class="form-section-title">BANKAUPPLÝSINGAR *</h3>
            
            <div class="form-group">
              <label class="form-label" for="bankName">Banki *</label>
              <input type="text" id="bankName" class="form-input" required>
            </div>
            
            <div class="form-group">
              <label class="form-label" for="bankRegistrationNo">Skrás. nr. *</label>
              <input type="text" id="bankRegistrationNo" class="form-input" required>
            </div>
            
            <div class="form-group">
              <label class="form-label" for="bankAccountNo">Konto nr. *</label>
              <input type="text" id="bankAccountNo" class="form-input" required>
            </div>
          </div>

          <!-- Rights Declaration -->
          <div class="form-section">
            <h3 class="form-section-title">${t('rights_warranty_title')}</h3>
            <div class="form-group">
              <label class="form-checkbox rights-terms-checkbox">
                <input type="checkbox" id="rightsTermsAccepted" required>
                <span style="line-height: 1.6;">
                  <strong style="display: block; margin-bottom: var(--spacing-sm);">Eg vátti hervið, at:</strong>
                  <ul style="margin: var(--spacing-sm) 0; padding-left: var(--spacing-lg); list-style-type: disc;">
                    <li style="margin-bottom: var(--spacing-xs);">Eg eri løgfrøðiligi eigarin (ella eri umboðandi) av hesum filmi og havi fullan rætt at leggja hann út á hendan pallin.</li>
                    <li style="margin-bottom: var(--spacing-xs);">Eg havi fingið til vega øll neyðug rættindi og loyvi frá øllum pørtum, sum hava lagt til verkið, íroknað (men ikki avmarkað til): leikarar, starvsfólk, tónleik, myndir/list/savnstilfar og staðsetingar.</li>
                    <li style="margin-bottom: var(--spacing-xs);">Eg taki á meg fulla ábyrgd, og Filmshúsið er undantikið øllum krøvum frá triðja parti viðvíkjandi hesum filmi.</li>
                    <li>Eg havi lisið og góðkent <a href="/rights-holder-terms" target="_blank" rel="noopener">Treytir fyri rættindahavarar</a>.</li>
                  </ul>
                </span>
              </label>
              <p class="form-help">${t('rights_terms_version_label')}: ${RIGHTS_TERMS_VERSION}</p>
            </div>
          </div>
          
          <!-- Distribution -->
          <div class="form-section">
            <h3 class="form-section-title">DISTRIBUTION</h3>

            <div class="form-group">
              <label class="checkbox-label" style="display: flex; align-items: flex-start; gap: var(--spacing-sm); cursor: pointer;">
                <input type="checkbox" id="geoRestricted" style="margin-top: 4px; flex-shrink: 0; width: 20px; height: 20px; cursor: pointer;">
                <span>Avmarka til Føroyar (FO)</span>
              </label>
              <p class="form-help">Tá ið hesin er virkin, kann filmurin bert síggjast í Føroyum.</p>
            </div>
          </div>

          <!-- Actions -->
          <div class="flex flex-gap-md mt-lg">
            <button type="submit" class="btn btn-primary" id="submit-btn">
              ${t('upload_button')}
            </button>
            <button type="button" class="btn btn-secondary" onclick="history.back()">
              ${t('action_cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  `;

  app.appendChild(main);
  initUploadForm();
}

function initUploadForm() {
  let _selectedFile = null;
  let selectedPosterFile = null;
  let selectedStillFile = null;
  let castCount = 1;
  let crewCount = 1;
  const roleCounts = {
    director: 1,
    screenwriter: 1,
    producer: 1,
    dop: 1,
    editor: 1,
    composer: 1,
  };
  const subtitlesToUpload = []; // Array of {file, language, label}

  // Auto-upload state
  let uploadedVideoId = null;
  let isUploading = false;
  let videoUploadComplete = false;
  let currentAssetsRecord = null;
  let currentUploadBackend = null;
  let activeAssetsUploadController = null;
  let pauseRequestedByUser = false;
  let uploadRecoveryPromise = Promise.resolve();

  const uploadZone = document.getElementById('upload-zone');
  const fileInput = document.getElementById('file-input');
  const fileInfo = document.getElementById('file-info');
  const fileName = document.getElementById('file-name');
  const fileSize = document.getElementById('file-size');
  const removeFileBtn = document.getElementById('remove-file');
  const form = document.getElementById('upload-form');
  const submitBtn = document.getElementById('submit-btn');
  const progressContainer = document.getElementById('upload-progress');
  const progressFill = document.getElementById('progress-fill');
  const progressText = document.getElementById('progress-text');

  function getStoredAssetsRecord() {
    const uploadId =
      localStorage.getItem('currentUploadAssetsUploadId') || localStorage.getItem('currentUploadSessionId');
    if (!uploadId) return null;

    try {
      return loadUploadRecord(localStorage, uploadId);
    } catch (error) {
      console.warn('Could not read the saved Assets upload record:', error);
      return null;
    }
  }

  function getChunkStatusUrl(createData) {
    if (createData.chunkStatusUrl) return createData.chunkStatusUrl;
    if (createData.statusUrl) return createData.statusUrl;
    return `/uploads/chunk/${encodeURIComponent(createData.sessionId)}/status`;
  }

  async function getStoredAssetsServerStatus(sessionId) {
    try {
      const response = await fetch(`${api.baseUrl}/uploads/chunk/${encodeURIComponent(sessionId)}/status`, {
        headers: {
          Accept: 'application/json',
          ...(await api.getAuthHeaders()),
        },
      });
      if (response.status === 404 || response.status === 410) {
        return { kind: 'unusable', status: null };
      }
      if (!response.ok) return { kind: 'unavailable', status: null };

      let status;
      try {
        status = await response.json();
      } catch (error) {
        console.warn('Assets returned an invalid saved-upload status:', error);
        return { kind: 'unusable', status: null };
      }
      if (status.sessionId !== undefined && status.sessionId !== sessionId) {
        return { kind: 'unusable', status: null };
      }
      return { kind: 'ok', status };
    } catch (error) {
      console.warn('Could not verify the older Assets upload:', error);
      return { kind: 'unavailable', status: null };
    }
  }

  function persistAssetsSessionKeys(createData, videoId, selectedFileName) {
    localStorage.setItem('currentUploadBackend', 'assets');
    localStorage.setItem('currentUploadCompleted', 'false');
    localStorage.setItem('currentUploadVideoId', videoId);
    localStorage.setItem('currentUploadFileName', selectedFileName);
    localStorage.setItem('currentUploadAssetsUploadId', createData.uploadId || createData.sessionId);
    localStorage.setItem('currentUploadAssetsVideoId', createData.assetsVideoId);
    localStorage.setItem('currentUploadSessionId', createData.sessionId);
  }

  function setConfirmedProgress(confirmedCount, totalChunks, complete = false) {
    const calculated = Math.floor((confirmedCount / totalChunks) * 100);
    const percent = complete ? 100 : Math.min(99, calculated);
    progressContainer.style.display = 'block';
    progressFill.style.width = `${percent}%`;
    progressFill.style.background = complete ? 'var(--color-success)' : '';
    progressText.textContent = complete
      ? '✅ Allir partarnir eru móttiknir. Til reiðar at senda inn.'
      : `Leggur upp: ${percent}% (${confirmedCount}/${totalChunks} partar váttaðir)`;
  }

  function safeUploadMessage(error) {
    const messages = {
      UPLOAD_AUTH_REQUIRED: 'Rita inn aftur og vel síðani somu fíluna fyri at halda fram.',
      UPLOAD_CONFIGURATION_ERROR: 'Uppløgustøðan samsvarar ikki við hesa fíluna. Set teg í samband við Filmshúsið.',
      UPLOAD_FILE_MISMATCH: 'Hetta er ikki sama fíla sum í fyrru uppløguni. Vel somu fíluna fyri at halda fram.',
      UPLOAD_FILE_UNREADABLE: 'Kagin fekk ikki lisið fíluna. Kanna fíluna og royn aftur.',
      UPLOAD_INCOMPLETE: 'Nakrir partar vanta enn. Uppløgan er sett í bíðistøðu og kann halda fram.',
      UPLOAD_PAUSED: 'Uppløgan er sett í bíðistøðu. Vel somu fíluna fyri at halda fram.',
      UPLOAD_REJECTED: 'Uppløgutænastan noktaði umbønini. Uppløgan er varðveitt.',
      UPLOAD_SESSION_EXPIRED: 'Uppløgutilfarið er útrunnið. Set teg í samband við Filmshúsið, áðrenn tú roynir aftur.',
      UPLOAD_STATUS_INVALID: 'Tað bar ikki til at vátta uppløguna. Uppløgan er varðveitt.',
      UPLOAD_STORAGE_ERROR: 'Kagin fekk ikki goymt uppløgustøðuna. Lat hesa síðuna vera opna og royn aftur.',
      UPLOAD_TEMPORARILY_UNAVAILABLE:
        'Uppløgutænastan svarar ikki beint nú. Uppløgan er sett í bíðistøðu og kann halda fram.',
    };
    const message =
      error instanceof UploadError
        ? messages[error.code] || messages.UPLOAD_REJECTED
        : 'Tað bar ikki til at leggja fíluna upp. Uppløgan er varðveitt, so tú kanst royna aftur.';
    return error instanceof UploadError && error.requestId ? `${message}\n\nTilvísing: ${error.requestId}` : message;
  }

  function resetSelectedFileUi() {
    _selectedFile = null;
    fileInput.value = '';
    fileInfo.style.display = 'none';
    uploadZone.style.display = 'flex';
  }

  function clearAssetsUploadSessionState(record = currentAssetsRecord || getStoredAssetsRecord()) {
    const uploadId =
      record?.uploadId ||
      localStorage.getItem('currentUploadAssetsUploadId') ||
      localStorage.getItem('currentUploadSessionId');
    if (uploadId) {
      try {
        removeUploadRecord(localStorage, uploadId);
      } catch (error) {
        console.warn('Could not remove the unusable Assets upload record:', error);
      }
    }

    [
      'currentUploadVideoId',
      'currentUploadFileName',
      'currentUploadBackend',
      'currentUploadCompleted',
      'currentUploadAssetsUploadId',
      'currentUploadAssetsVideoId',
      'currentUploadSessionId',
    ].forEach((key) => {
      localStorage.removeItem(key);
    });

    uploadedVideoId = null;
    videoUploadComplete = false;
    currentAssetsRecord = null;
    currentUploadBackend = null;
  }

  function reconcileAssetsServerStatus(record, status) {
    if (status?.videoId !== undefined && status.videoId !== record.assetsVideoId) {
      throw new UploadError('UPLOAD_STATUS_INVALID');
    }

    const normalized = normalizeUploadStatus(status, record.totalChunks);
    if (Number.isInteger(status.uploadedCount) && status.uploadedCount !== normalized.confirmedCount) {
      throw new UploadError('UPLOAD_STATUS_INVALID');
    }
    return normalized;
  }

  function showFreshAssetsUploadUi() {
    resetSelectedFileUi();
    progressContainer.style.display = 'block';
    progressFill.style.width = '0%';
    progressFill.style.background = 'var(--color-warning)';
    progressText.textContent = 'Vel videofíluna fyri at byrja eina nýggja uppløgu.';
    submitBtn.disabled = false;
    submitBtn.innerHTML = '📤 Vel videofílu';
  }

  function clearUploadStateAfterSuccessfulFinalize() {
    const completedVideoId = uploadedVideoId;
    clearAssetsUploadSessionState();
    if (completedVideoId) api.clearUploadProgress(completedVideoId);
    localStorage.removeItem('uploadFormData');
  }

  async function finalizeCurrentVideo(assetsSessionId) {
    let authenticationRefreshed = false;

    for (let attempt = 1; attempt <= 4; attempt++) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort('timeout'), 240_000);
      let response;

      try {
        response = await fetch(`${api.baseUrl}/videos/${uploadedVideoId}/finalize`, {
          method: 'POST',
          headers: {
            ...(await api.getAuthHeaders()),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            assetsSessionId: assetsSessionId || undefined,
          }),
          signal: controller.signal,
        });
      } catch (cause) {
        if (attempt === 4) {
          throw new UploadError('UPLOAD_TEMPORARILY_UNAVAILABLE', {
            retryable: true,
            cause,
          });
        }
      } finally {
        clearTimeout(timeout);
      }

      if (response?.ok) return;

      if (response?.status === 401 && !authenticationRefreshed) {
        authenticationRefreshed = true;
        await refreshToken().catch((cause) => {
          throw new UploadError('UPLOAD_AUTH_REQUIRED', {
            retryable: true,
            status: 401,
            cause,
          });
        });
        attempt--;
        continue;
      }

      const status = response?.status || null;
      const retryable = response === undefined || status === 408 || status === 409 || status === 429 || status >= 500;

      if (!retryable || attempt === 4) {
        throw new UploadError(
          status === 401 ? 'UPLOAD_AUTH_REQUIRED' : retryable ? 'UPLOAD_TEMPORARILY_UNAVAILABLE' : 'UPLOAD_REJECTED',
          {
            retryable,
            status,
            requestId: response?.headers.get('x-request-id') || response?.headers.get('x-correlation-id'),
          },
        );
      }

      const retryAfterSeconds = Number(response?.headers.get('retry-after'));
      const serverDelay = Number.isFinite(retryAfterSeconds) ? retryAfterSeconds * 1_000 : 0;
      const backoff = Math.min(30_000, 1_000 * 2 ** (attempt - 1));
      await new Promise((resolve) => setTimeout(resolve, Math.min(30_000, Math.max(serverDelay, backoff))));
    }
  }

  // Image file elements
  const posterFileInput = document.getElementById('posterFile');
  const stillFileInput = document.getElementById('stillFile');
  const selectPosterBtn = document.getElementById('selectPosterBtn');
  const selectStillBtn = document.getElementById('selectStillBtn');
  const posterFileName = document.getElementById('posterFileName');
  const stillFileName = document.getElementById('stillFileName');

  // File upload handlers
  uploadZone.addEventListener('click', () => fileInput.click());

  uploadZone.addEventListener('dragover', (e) => {
    e.preventDefault();
    uploadZone.style.borderColor = 'var(--primary-color)';
    uploadZone.style.background = 'rgba(0, 119, 192, 0.05)';
  });

  uploadZone.addEventListener('dragleave', () => {
    uploadZone.style.borderColor = 'rgba(255, 255, 255, 0.1)';
    uploadZone.style.background = 'rgba(255, 255, 255, 0.02)';
  });

  uploadZone.addEventListener('drop', (e) => {
    e.preventDefault();
    uploadZone.style.borderColor = 'rgba(255, 255, 255, 0.1)';
    uploadZone.style.background = 'rgba(255, 255, 255, 0.02)';

    if (e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  });

  fileInput.addEventListener('change', (e) => {
    if (e.target.files.length > 0) {
      handleFileSelect(e.target.files[0]);
    }
  });

  removeFileBtn.addEventListener('click', async () => {
    if (uploadedVideoId && currentUploadBackend === 'assets') {
      if (videoUploadComplete) {
        await showModal(
          'Videofílan er longu løgd upp og váttað. Send skrásetingina inn fyri at gera hana lidna.',
          'info',
          'Videofílan er løgd upp',
        );
        return;
      }

      const confirmed = await showConfirm(
        'Vilt tú seta uppløguna í bíðistøðu?\n\n' + 'Støðan verður goymd. Vel somu fíluna seinni fyri at halda fram.',
        'Set uppløguna í bíðistøðu',
      );

      if (!confirmed) return;

      pauseRequestedByUser = true;
      activeAssetsUploadController?.pause();
      resetSelectedFileUi();
      progressContainer.style.display = 'block';
      progressFill.style.background = 'var(--color-warning)';
      progressText.textContent = '⏸ Uppløgan er sett í bíðistøðu. Vel somu fíluna fyri at halda fram.';
      submitBtn.disabled = true;
      submitBtn.innerHTML = '⏸ Uppløga í bíðistøðu';
      return;
    }

    if (isUploading) {
      await showModal(
        'Hendan eldri uppløgan kann ikki setast í bíðistøðu. Bíða til hon er liðug.',
        'warning',
        'Uppløga í gongd',
      );
      return;
    }

    // Preserve the existing direct-Azure behavior for legacy uploads.
    if (uploadedVideoId && !isUploading) {
      const confirmed = await showConfirm(
        'Videofílan er longu løgd upp.\n\n' + 'Um tú strikar hana, mást tú byrja umaftur.\n\n' + 'Ert tú vís/ur?',
        'Strika uppløgu',
      );

      if (!confirmed) return;

      uploadedVideoId = null;
      videoUploadComplete = false;
      localStorage.removeItem('currentUploadVideoId');
      localStorage.removeItem('currentUploadFileName');
      localStorage.removeItem('currentUploadBackend');
      localStorage.removeItem('currentUploadCompleted');
    }

    // Reset file selection and UI
    resetSelectedFileUi();
    progressContainer.style.display = 'none';
    progressFill.style.width = '0%';
    progressFill.style.background = '';
    submitBtn.disabled = false;
    submitBtn.innerHTML = '📤 Legg upp film';
  });

  // Image file selection handlers
  selectPosterBtn.addEventListener('click', () => posterFileInput.click());
  selectStillBtn.addEventListener('click', () => stillFileInput.click());

  posterFileInput.addEventListener('change', (e) => {
    if (e.target.files.length > 0) {
      selectedPosterFile = e.target.files[0];
      posterFileName.textContent = `${selectedPosterFile.name} (${formatFileSize(selectedPosterFile.size)}, ${selectedPosterFile.type || 'unknown'})`;
      // Clear URL input if file is selected
      document.getElementById('posterUrl').value = '';
    }
  });

  stillFileInput.addEventListener('change', (e) => {
    if (e.target.files.length > 0) {
      selectedStillFile = e.target.files[0];
      stillFileName.textContent = `${selectedStillFile.name} (${formatFileSize(selectedStillFile.size)}, ${selectedStillFile.type || 'unknown'})`;
      // Clear URL input if file is selected
      document.getElementById('stillUrl').value = '';
    }
  });

  function getRightsTermsPayload() {
    return {
      rightsTermsAccepted: true,
      rightsTermsVersion: RIGHTS_TERMS_VERSION,
      rightsTermsLanguage: getLanguage(),
    };
  }

  async function ensureRightsTermsAccepted() {
    const rightsTermsCheckbox = document.getElementById('rightsTermsAccepted');
    if (rightsTermsCheckbox?.checked) return true;

    await showModal(t('rights_terms_required'), 'warning', t('rights_warranty_title'));
    rightsTermsCheckbox?.focus();
    return false;
  }

  async function handleFileSelect(file) {
    await uploadRecoveryPromise;

    if (!(await ensureRightsTermsAccepted())) {
      fileInput.value = '';
      return;
    }

    if (isUploading) return;

    _selectedFile = file;
    fileName.textContent = file.name;
    fileSize.textContent = formatFileSize(file.size);
    const fileFormat = document.getElementById('file-format');
    if (fileFormat) {
      const ext = file.name.split('.').pop().toUpperCase();
      fileFormat.textContent = `(${file.type || ext})`;
    }
    fileInfo.style.display = 'block';
    uploadZone.style.display = 'none';

    // Start background upload immediately
    await startBackgroundUpload(file);
  }

  // Background upload function
  async function startBackgroundUpload(file) {
    try {
      isUploading = true;
      videoUploadComplete = false;
      pauseRequestedByUser = false;
      submitBtn.disabled = true;
      submitBtn.innerHTML = '⏳ Leggur upp... Bíða við';
      progressContainer.style.display = 'block';
      progressText.textContent = 'Byrjar uppløgu...';

      const storedRecord = getStoredAssetsRecord();
      const storedVideoId = localStorage.getItem('currentUploadVideoId');
      const storedBackend = localStorage.getItem('currentUploadBackend');

      if (storedRecord && storedRecord.videoId === storedVideoId) {
        uploadedVideoId = storedRecord.videoId;
        currentUploadBackend = 'assets';
        currentAssetsRecord = storedRecord;
        console.log(`📤 Resuming Assets upload for videoId: ${uploadedVideoId}`);
        await uploadFileToAssets(file, { record: storedRecord });
      } else {
        const hasStaleAssetsState =
          storedRecord ||
          storedBackend === 'assets' ||
          localStorage.getItem('currentUploadAssetsUploadId') ||
          localStorage.getItem('currentUploadAssetsVideoId') ||
          localStorage.getItem('currentUploadSessionId');
        if (hasStaleAssetsState) {
          clearAssetsUploadSessionState(storedRecord);
        }

        // Step 1: Create video in API (get videoId and upload session).
        const createResponse = await fetch(`${api.baseUrl}/videos`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(await api.getAuthHeaders()),
          },
          body: JSON.stringify({
            fileName: file.name,
            fileSize: file.size,
            mimeType: file.type,
            metadata: getRightsTermsPayload(), // Full metadata is updated on submit
          }),
        });

        if (!createResponse.ok) {
          const retryable =
            createResponse.status === 408 || createResponse.status === 429 || createResponse.status >= 500;
          throw new UploadError(
            createResponse.status === 401
              ? 'UPLOAD_AUTH_REQUIRED'
              : retryable
                ? 'UPLOAD_TEMPORARILY_UNAVAILABLE'
                : 'UPLOAD_REJECTED',
            { status: createResponse.status, retryable },
          );
        }

        let createData;
        try {
          createData = await createResponse.json();
        } catch (cause) {
          throw new UploadError('UPLOAD_TEMPORARILY_UNAVAILABLE', {
            retryable: true,
            cause,
          });
        }

        uploadedVideoId = createData.videoId;
        console.log(`📤 Starting background upload for videoId: ${uploadedVideoId}`);

        // Step 2: Upload file — Assets chunked or legacy Azure.
        if (createData.uploadType === 'assets-chunked') {
          currentUploadBackend = 'assets';
          persistAssetsSessionKeys(createData, uploadedVideoId, file.name);
          await uploadFileToAssets(file, { createData });
        } else {
          currentUploadBackend = 'legacy';
          localStorage.setItem('currentUploadBackend', 'legacy');
          localStorage.setItem('currentUploadCompleted', 'false');
          localStorage.setItem('currentUploadVideoId', uploadedVideoId);
          localStorage.setItem('currentUploadFileName', file.name);
          await uploadFileToBlobStorage(file, createData.uploadUrl, uploadedVideoId);
        }
      }

      // Upload complete!
      isUploading = false;
      videoUploadComplete = true;
      localStorage.setItem('currentUploadCompleted', 'true');
      submitBtn.disabled = false;
      submitBtn.innerHTML = '📤 Send inn film';
      progressText.textContent = '✅ Uppløga fullførd! Fyll út skrásetingina og send inn.';
      progressFill.style.width = '100%';
      progressFill.style.background = 'var(--color-success)';

      console.log(`✅ Background upload complete for videoId: ${uploadedVideoId}`);
    } catch (error) {
      console.error('Background upload failed:', error);
      isUploading = false;
      videoUploadComplete = false;

      if (currentUploadBackend === 'assets') {
        if (error instanceof UploadError && error.code === 'UPLOAD_SESSION_EXPIRED') {
          clearAssetsUploadSessionState();
          resetSelectedFileUi();
          progressContainer.style.display = 'block';
          progressFill.style.width = '0%';
          progressFill.style.background = 'var(--color-warning)';
          progressText.textContent = '⚠️ Uppløgan er útrunnin. Vel fíluna fyri at byrja av nýggjum.';
          submitBtn.disabled = false;
          submitBtn.innerHTML = '📤 Vel videofílu';
          await showModal(
            'Fyrra uppløgan kann ikki halda fram. Vel videofíluna fyri at byrja eina nýggja uppløgu.',
            'warning',
            'Byrja av nýggjum',
          );
          return;
        }

        localStorage.setItem('currentUploadCompleted', 'false');
        currentAssetsRecord = getStoredAssetsRecord() || currentAssetsRecord;
        const safeMessage = safeUploadMessage(error);
        const wasUserPause = pauseRequestedByUser && error instanceof UploadError && error.code === 'UPLOAD_PAUSED';

        resetSelectedFileUi();
        submitBtn.disabled = true;
        submitBtn.innerHTML = '⏸ Vel somu fíluna og halt fram';
        progressContainer.style.display = 'block';
        progressFill.style.background = 'var(--color-warning)';
        progressText.textContent = `⏸ ${safeMessage.split('\n')[0]}`;

        if (!wasUserPause) {
          await showModal(
            `${safeMessage}\n\nVel somu fíluna aftur fyri at halda fram frá seinasta váttaða fíluparti.`,
            error instanceof UploadError && !error.retryable ? 'error' : 'warning',
            'Uppløgan er varðveitt',
          );
        }
      } else {
        submitBtn.disabled = false;
        submitBtn.innerHTML = '📤 Legg upp film';
        progressText.textContent = '❌ Uppløgan miseydnaðist. Kanna sambandið og royn aftur.';
        progressFill.style.background = 'var(--color-error)';
        progressContainer.style.display = 'block';

        await showModal(
          'Tað bar ikki til at leggja videofíluna upp. Kanna sambandið og royn aftur.',
          'error',
          'Uppløgan miseydnaðist',
        );

        // The legacy direct-Azure path keeps its existing restart behavior.
        uploadedVideoId = null;
        currentUploadBackend = null;
        localStorage.removeItem('currentUploadVideoId');
        localStorage.removeItem('currentUploadFileName');
        localStorage.removeItem('currentUploadBackend');
        localStorage.removeItem('currentUploadCompleted');
      }
    } finally {
      activeAssetsUploadController = null;
      pauseRequestedByUser = false;
    }
  }

  // Upload file to Assets Platform via chunked upload
  async function uploadFileToAssets(file, { createData, record } = {}) {
    const session = createData
      ? {
          ...createData,
          uploadId: createData.uploadId || createData.sessionId,
          chunkStatusUrl: getChunkStatusUrl(createData),
        }
      : undefined;

    if (session) {
      console.log(`Assets chunked upload: ${session.totalChunks} chunks of ${session.chunkSize} bytes`);
    }

    activeAssetsUploadController = createUploadController();
    const result = await uploadAssetsFile({
      file,
      session,
      record,
      storage: localStorage,
      apiBaseUrl: api.baseUrl,
      signal: activeAssetsUploadController.signal,
      dependencies: {
        fetch: (...args) => fetch(...args),
        getAuthHeaders: () => api.getAuthHeaders(),
        forceAuthRefresh: () => refreshToken(),
      },
      retry: {
        maxAttempts: 6,
        attemptTimeoutMs: 240_000,
        maxDelayMs: 30_000,
      },
      onProgress: ({ confirmedCount, totalChunks, percent, reconciled }) => {
        setConfirmedProgress(confirmedCount, totalChunks, reconciled && percent === 100);
      },
    });

    currentAssetsRecord = result.record;
    localStorage.setItem('currentUploadAssetsUploadId', result.record.uploadId);
    localStorage.setItem('currentUploadAssetsVideoId', result.record.assetsVideoId);
    localStorage.setItem('currentUploadSessionId', result.record.sessionId);
  }

  // Upload file to blob storage with progress tracking
  async function uploadFileToBlobStorage(file, uploadUrl, videoId) {
    const CHUNK_SIZE = 4 * 1024 * 1024; // 4 MB chunks
    const MAX_SINGLE_UPLOAD_SIZE = 256 * 1024 * 1024; // 256 MB

    if (file.size <= MAX_SINGLE_UPLOAD_SIZE) {
      // Small file - use simple PUT
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percentComplete = Math.round((e.loaded / e.total) * 100);
            progressFill.style.width = `${percentComplete}%`;
            progressText.textContent = `Leggur upp: ${percentComplete}%`;
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            progressFill.style.width = '100%';
            progressText.textContent = 'Fullførur...';
            resolve();
          } else {
            reject(new Error(`Failed to upload: ${xhr.statusText}`));
          }
        });

        xhr.addEventListener('error', () => reject(new Error('Network error during upload')));
        xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));

        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
        xhr.setRequestHeader('Content-Type', file.type || 'video/mp4');
        xhr.send(file);
      });
    } else {
      // Large file - use chunked upload with retry logic
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

      // Check for existing upload progress (resumable upload)
      const savedProgress = api.getUploadProgress(videoId);
      const uploadedChunks = savedProgress?.uploadedChunks || [];
      const blockIds = savedProgress?.blockIds || [];

      if (savedProgress && (savedProgress.fileSize !== file.size || savedProgress.totalChunks !== totalChunks)) {
        console.warn(`⚠️ Saved progress doesn't match current file, starting fresh`);
        uploadedChunks.length = 0;
        blockIds.length = 0;
        api.clearUploadProgress(videoId);
      }

      const isResuming = uploadedChunks.length > 0;
      if (isResuming) {
        console.log(`🔄 Resuming upload from chunk ${uploadedChunks.length}/${totalChunks}`);
      }

      // Start keepalive
      const stopKeepalive = api.startKeepalive(videoId);

      try {
        for (let i = 0; i < totalChunks; i++) {
          // Skip already uploaded chunks
          if (uploadedChunks.includes(i)) {
            const progress = Math.round(((i + 1) / totalChunks) * 100);
            progressFill.style.width = `${progress}%`;
            progressText.textContent = `Leggur upp: ${progress}% (framhaldur...)`;
            continue;
          }

          const start = i * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);
          const chunk = file.slice(start, end);

          // Generate or reuse block ID
          const blockId = blockIds[i] || btoa(String(i).padStart(6, '0'));
          if (!blockIds[i]) blockIds[i] = blockId;

          // Build chunk URL
          const blockUrl = `${uploadUrl + (uploadUrl.includes('?') ? '&' : '?')}comp=block&blockid=${encodeURIComponent(blockId)}`;

          // Upload block with retry logic
          await api.uploadChunkWithRetry(chunk, blockUrl, videoId, i);

          // Mark chunk as uploaded and save progress
          uploadedChunks.push(i);
          api.saveUploadProgress(videoId, file.name, file.size, totalChunks, uploadedChunks, blockIds);

          // Update progress UI
          const progress = Math.round(((i + 1) / totalChunks) * 100);
          progressFill.style.width = `${progress}%`;
          progressText.textContent = `Leggur upp: ${progress}%`;
        }

        console.log(`✅ All chunks uploaded, committing blocks...`);
        progressText.textContent = 'Fullførur uppløgu...';

        // Commit all blocks
        const blockListXml =
          '<?xml version="1.0" encoding="utf-8"?><BlockList>' +
          blockIds.map((id) => `<Latest>${id}</Latest>`).join('') +
          '</BlockList>';

        const commitUrl = `${uploadUrl + (uploadUrl.includes('?') ? '&' : '?')}comp=blocklist`;
        const commitResponse = await fetch(commitUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/xml',
            'x-ms-blob-content-type': file.type || 'video/mp4',
          },
          body: blockListXml,
        });

        if (!commitResponse.ok) {
          throw new Error(`Failed to commit blocks: ${commitResponse.statusText}`);
        }

        // Clear upload progress after successful commit
        api.clearUploadProgress(videoId);
      } finally {
        stopKeepalive();
      }
    }
  }

  function formatFileSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  }

  // Subtitle management
  const subtitleList = document.getElementById('subtitle-list');
  const addSubtitleBtn = document.getElementById('add-subtitle-btn');

  function renderSubtitleList() {
    if (subtitlesToUpload.length === 0) {
      subtitleList.innerHTML =
        '<p style="color: var(--color-cloud-grey); font-size: 0.875rem; margin: 0;">Ongir undirtekstir enn</p>';
      return;
    }

    subtitleList.innerHTML = subtitlesToUpload
      .map(
        (subtitle, index) => `
      <div class="subtitle-item" data-index="${index}">
        <div>
          <strong>${subtitle.label}</strong> (${subtitle.language})
          <div style="font-size: 0.875rem; color: var(--color-cloud-grey);">${subtitle.file.name}</div>
        </div>
        <button type="button" class="btn btn-small btn-danger" onclick="removeSubtitle(${index})">Strika</button>
      </div>
    `,
      )
      .join('');
  }

  window.removeSubtitle = (index) => {
    subtitlesToUpload.splice(index, 1);
    renderSubtitleList();
  };

  addSubtitleBtn.addEventListener('click', async () => {
    // Create file input
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.srt,.vtt,.ass,.ssa';
    fileInput.style.display = 'none';
    document.body.appendChild(fileInput);

    fileInput.addEventListener('change', async (e) => {
      const file = e.target.files[0];
      if (!file) {
        document.body.removeChild(fileInput);
        return;
      }

      // Show language selection modal
      const languageCode = await showLanguageSelector();
      if (!languageCode) {
        document.body.removeChild(fileInput);
        return;
      }

      const language = SUBTITLE_LANGUAGES.find((l) => l.code === languageCode);
      if (!language) {
        await showModal('Invalid language selected', 'error', 'Error');
        document.body.removeChild(fileInput);
        return;
      }

      subtitlesToUpload.push({
        file,
        language: language.code,
        label: language.label,
      });

      renderSubtitleList();
      document.body.removeChild(fileInput);
    });

    fileInput.click();
  });

  function showLanguageSelector() {
    return new Promise((resolve) => {
      const modal = document.createElement('div');
      modal.className = 'custom-modal-overlay';

      const closeModal = (result) => {
        modal.remove();
        resolve(result);
      };

      modal.innerHTML = `
        <div class="modal" style="max-width: 500px;">
          <div class="modal-header">
            <h2>Vel mál</h2>
            <button class="modal-close">×</button>
          </div>
          <div class="modal-content">
            <input type="text" id="language-search" class="form-input" placeholder="Leita eftir málum..." style="margin-bottom: var(--spacing-sm);">
            <div id="language-list" style="max-height: 300px; overflow-y: auto;">
              ${SUBTITLE_LANGUAGES.map(
                (lang) => `
                <div class="language-option" data-code="${lang.code}" style="padding: var(--spacing-sm); cursor: pointer; border-radius: var(--radius-sm); margin-bottom: 4px; background: rgba(13, 17, 23, 0.4);">
                  <strong>${lang.code}</strong> - ${lang.label}
                </div>
              `,
              ).join('')}
            </div>
          </div>
        </div>
      `;

      document.body.appendChild(modal);

      modal.querySelector('.modal-close').addEventListener('click', () => closeModal(null));
      modal.addEventListener('click', (e) => {
        if (e.target === modal) closeModal(null);
      });

      const searchInput = modal.querySelector('#language-search');
      const languageList = modal.querySelector('#language-list');

      languageList.addEventListener('click', (e) => {
        const option = e.target.closest('.language-option');
        if (option) {
          closeModal(option.dataset.code);
        }
      });

      searchInput.addEventListener('input', (e) => {
        const term = e.target.value.toLowerCase();
        const filtered = filterLanguages(term);
        languageList.innerHTML = filtered
          .map(
            (lang) => `
          <div class="language-option" data-code="${lang.code}" style="padding: var(--spacing-sm); cursor: pointer; border-radius: var(--radius-sm); margin-bottom: 4px; background: rgba(13, 17, 23, 0.4);">
            <strong>${lang.code}</strong> - ${lang.label}
          </div>
        `,
          )
          .join('');
      });

      searchInput.focus();
    });
  }

  renderSubtitleList();

  // Add role member (director, screenwriter, producer, etc.)
  document.querySelectorAll('.add-role-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const role = btn.dataset.role;
      const container = document.getElementById(`${role}-container`);
      const idx = roleCounts[role];
      const div = document.createElement('div');
      div.className = 'name-input-group mt-sm';
      div.innerHTML = `
        <input type="text" id="${role}${idx}FirstName" class="form-input" placeholder="${t('placeholder_firstname')}">
        <input type="text" id="${role}${idx}MiddleName" class="form-input" placeholder="${t('placeholder_middlename')}">
        <input type="text" id="${role}${idx}LastName" class="form-input" placeholder="${t('placeholder_lastname')}">
      `;
      container.appendChild(div);
      roleCounts[role]++;
    });
  });

  // Add cast member
  document.getElementById('add-cast').addEventListener('click', () => {
    const container = document.getElementById('cast-container');
    const div = document.createElement('div');
    div.className = 'name-input-group mt-sm';
    div.innerHTML = `
      <input type="text" id="cast${castCount}FirstName" class="form-input" placeholder="${t('placeholder_firstname')}">
      <input type="text" id="cast${castCount}MiddleName" class="form-input" placeholder="${t('placeholder_middlename')}">
      <input type="text" id="cast${castCount}LastName" class="form-input" placeholder="${t('placeholder_lastname')}">
    `;
    container.appendChild(div);
    castCount++;
  });

  // Add crew member
  document.getElementById('add-crew').addEventListener('click', () => {
    const container = document.getElementById('crew-container');
    const div = document.createElement('div');
    div.className = 'name-input-group mt-sm';
    div.innerHTML = `
      <select id="crew${crewCount}Role" class="form-input" style="min-width: 200px;">
        <option value="">Vel leiklut...</option>
        <option value="production_designer">Production Designer / Framleiðslusniðgevi</option>
        <option value="costume_designer">Costume Designer / Búnasniðgevi</option>
        <option value="makeup_artist">Makeup Artist / Sminkulistafólk</option>
        <option value="sound_recordist">Sound Recordist / Ljóðmeistari</option>
        <option value="sound_designer">Sound Designer / Ljóðsniðgevi</option>
        <option value="gaffer">Gaffer / Ljósmeistari</option>
        <option value="key_grip">Key Grip / Høvuðsgrippur</option>
        <option value="colourist">Colourist / Litviðgeri</option>
      </select>
      <input type="text" id="crew${crewCount}FirstName" class="form-input" placeholder="${t('placeholder_firstname')}">
      <input type="text" id="crew${crewCount}MiddleName" class="form-input" placeholder="${t('placeholder_middlename')}">
      <input type="text" id="crew${crewCount}LastName" class="form-input" placeholder="${t('placeholder_lastname')}">
    `;
    container.appendChild(div);
    crewCount++;
  });

  // Screenings & Awards management
  let screeningCount = 0;
  const screeningsContainer = document.getElementById('screenings-container');
  const addScreeningBtn = document.getElementById('add-screening');

  function addScreeningEntry(data = {}) {
    if (screeningCount >= 6) return;
    const idx = screeningCount;
    const div = document.createElement('div');
    div.className = 'screening-entry';
    div.style.cssText = 'padding: 12px; background: rgba(0, 119, 192, 0.05); border-radius: 8px; margin-bottom: 8px;';
    div.innerHTML = `
      <div style="display: flex; gap: 8px; flex-wrap: wrap; align-items: center;">
        <input type="text" id="screening${idx}Festival" class="form-input" placeholder="Filmsfestival" value="${data.festival || ''}" style="flex: 2; min-width: 180px;">
        <input type="text" id="screening${idx}Location" class="form-input" placeholder="Staður / Land" value="${data.location || ''}" style="flex: 1; min-width: 120px;">
        <input type="number" id="screening${idx}Year" class="form-input" placeholder="Ár" value="${data.year || ''}" style="width: 80px;">
        <input type="text" id="screening${idx}Awards" class="form-input" placeholder="Virðislønir (valfrí)" value="${data.awards || ''}" style="flex: 2; min-width: 180px;">
        <button type="button" class="btn btn-small btn-danger" onclick="this.parentElement.parentElement.remove()">✕</button>
      </div>
    `;
    screeningsContainer.appendChild(div);
    screeningCount++;
    if (screeningCount >= 6) addScreeningBtn.style.display = 'none';
  }

  addScreeningBtn.addEventListener('click', () => addScreeningEntry());

  function getScreeningsArray() {
    const entries = screeningsContainer.querySelectorAll('.screening-entry');
    const screenings = [];
    entries.forEach((entry) => {
      const inputs = entry.querySelectorAll('input');
      const festival = inputs[0]?.value?.trim();
      const location = inputs[1]?.value?.trim();
      const year = parseInt(inputs[2]?.value, 10) || null;
      const awards = inputs[3]?.value?.trim();
      if (festival || location || awards) {
        screenings.push({
          festival: festival || '',
          location: location || '',
          year,
          awards: awards || '',
        });
      }
    });
    return screenings.length > 0 ? screenings : null;
  }

  // Character counters for textareas
  const loglineFo = document.getElementById('loglineFo');
  const loglineEn = document.getElementById('loglineEn');
  const synopsisFo = document.getElementById('synopsisFo');
  const synopsisEn = document.getElementById('synopsisEn');

  [loglineFo, loglineEn, synopsisFo, synopsisEn].forEach((textarea) => {
    if (textarea) {
      const counterId = `${textarea.id}-count`;
      const counter = document.getElementById(counterId);
      textarea.addEventListener('input', () => {
        if (counter) counter.textContent = textarea.value.length;
      });
    }
  });

  // Pricing type change handler
  const pricingTypeSelect = document.getElementById('pricingType');
  const pricingTierGroup = document.getElementById('pricing-tier-group');
  const pricingTierSelect = document.getElementById('pricingTier');
  const customPriceGroup = document.getElementById('custom-price-group');
  const rentalDurationGroup = document.getElementById('rental-duration-group');
  const bankInfoSection = document.getElementById('bank-info-section');
  const bankNameInput = document.getElementById('bankName');
  const bankRegistrationNoInput = document.getElementById('bankRegistrationNo');
  const bankAccountNoInput = document.getElementById('bankAccountNo');
  const categorySelect = document.getElementById('category');

  function updatePricingFields() {
    const pricingType = pricingTypeSelect.value;
    const category = categorySelect.value;

    if (pricingType === 'free') {
      pricingTierGroup.style.display = 'none';
      customPriceGroup.style.display = 'none';
      rentalDurationGroup.style.display = 'none';
      bankInfoSection.style.display = 'none';
      // Remove required attribute from bank fields
      bankNameInput.removeAttribute('required');
      bankRegistrationNoInput.removeAttribute('required');
      bankAccountNoInput.removeAttribute('required');
    } else {
      // Rental duration is always hidden (fixed at 48 hours)
      rentalDurationGroup.style.display = 'none';
      // Show bank info for paid content
      bankInfoSection.style.display = 'block';
      // Add required attribute back to bank fields
      bankNameInput.setAttribute('required', 'required');
      bankRegistrationNoInput.setAttribute('required', 'required');
      bankAccountNoInput.setAttribute('required', 'required');

      if (pricingType === 'custom') {
        pricingTierGroup.style.display = 'none';
        customPriceGroup.style.display = 'block';
      } else if (pricingType === 'standard') {
        customPriceGroup.style.display = 'none';
        pricingTierGroup.style.display = 'block';

        // Populate pricing tier options based on category
        pricingTierSelect.innerHTML = '<option value="">Vel prísfokk...</option>';

        if (category === 'short') {
          pricingTierSelect.innerHTML += `
          <option value="short_under_3mo">Yngri enn 3 mðr.: 29 kr</option>
          <option value="short_under_1yr">Yngri enn 1 ár: 19 kr</option>
          <option value="short_under_4yr">Yngri enn 4 ár: 7 kr</option>
          <option value="short_over_4yr">Eldri enn 4 ár: 0 kr</option>
        `;
        } else if (category === 'feature') {
          pricingTierSelect.innerHTML += `
          <option value="feature_under_2yr">Yngri enn 2 ár: 59 kr</option>
          <option value="feature_over_2yr">Eldri enn 2 ár: 39 kr</option>
          <option value="feature_over_4yr">Eldri enn 4 ár: 0 kr</option>
        `;
        } else if (category === 'documentary_short') {
          pricingTierSelect.innerHTML += `
          <option value="doc_short_under_1yr">Yngri enn 1 ár: 19 kr</option>
          <option value="doc_short_under_4yr">Yngri enn 4 ár: 7 kr</option>
          <option value="doc_short_over_4yr">Eldri enn 4 ár: 0 kr</option>
        `;
        } else if (category === 'documentary_long') {
          pricingTierSelect.innerHTML += `
          <option value="doc_long_under_2yr">Yngri enn 2 ár: 59 kr</option>
          <option value="doc_long_under_4yr">Yngri enn 4 ár: 39 kr</option>
          <option value="doc_long_over_4yr">Eldri enn 4 ár: 0 kr</option>
        `;
        }
      }
    }
  }

  pricingTypeSelect.addEventListener('change', updatePricingFields);
  categorySelect.addEventListener('change', updatePricingFields);

  // Auto-save form data to localStorage
  const formFields = [
    'category',
    'originalTitle',
    'internationalTitle',
    'loglineFo',
    'loglineEn',
    'synopsisFo',
    'synopsisEn',
    'director',
    'screenwriter',
    'producer',
    'dop',
    'editor',
    'composer',
    'productionCompany',
    'coproductionCompany',
    'runtimeHours',
    'runtimeMinutes',
    'language',
    'subtitles',
    'productionYear',
    'imdbLink',
    'pricingType',
    'pricingTier',
    'customPrice',
    'rentalDuration',
    'bankName',
    'bankRegistrationNo',
    'bankAccountNo',
    'encode4K',
    'geoRestricted',
    'ageRating',
  ];

  let autoSaveTimeout;
  function autoSaveFormData() {
    clearTimeout(autoSaveTimeout);
    autoSaveTimeout = setTimeout(() => {
      const formData = {};
      formFields.forEach((fieldId) => {
        const field = document.getElementById(fieldId);
        if (field) {
          if (field.type === 'checkbox') {
            formData[fieldId] = field.checked;
          } else {
            formData[fieldId] = field.value;
          }
        }
      });

      // Save to localStorage
      localStorage.setItem('uploadFormData', JSON.stringify(formData));
      console.log('💾 Form auto-saved');
    }, 500); // Save 500ms after user stops typing
  }

  // Attach auto-save to all form fields
  formFields.forEach((fieldId) => {
    const field = document.getElementById(fieldId);
    if (field) {
      field.addEventListener('input', autoSaveFormData);
      field.addEventListener('change', autoSaveFormData);
    }
  });

  // Restore form data on page load
  function restoreFormData() {
    const savedData = localStorage.getItem('uploadFormData');
    if (!savedData) return false;

    try {
      const formData = JSON.parse(savedData);

      Object.keys(formData).forEach((fieldId) => {
        const field = document.getElementById(fieldId);
        if (field) {
          if (field.type === 'checkbox') {
            field.checked = formData[fieldId];
          } else {
            field.value = formData[fieldId];
          }
        }
      });

      console.log('📂 Form data restored from auto-save');
      return true;
    } catch (error) {
      console.error('Failed to restore form data:', error);
      return false;
    }
  }

  // Check for interrupted upload on page load
  uploadRecoveryPromise = (async () => {
    const currentUploadVideoId = localStorage.getItem('currentUploadVideoId');
    const currentUploadFileName = localStorage.getItem('currentUploadFileName');
    const storedBackend = localStorage.getItem('currentUploadBackend');
    const storedUploadComplete = localStorage.getItem('currentUploadCompleted') === 'true';
    const hasFormData = restoreFormData();
    const storedAssetsRecord = getStoredAssetsRecord();
    const assetsSessionId = storedAssetsRecord?.sessionId || localStorage.getItem('currentUploadSessionId');
    const hasAssetsState = Boolean(
      storedAssetsRecord ||
        storedBackend === 'assets' ||
        localStorage.getItem('currentUploadAssetsUploadId') ||
        localStorage.getItem('currentUploadAssetsVideoId') ||
        assetsSessionId,
    );

    if (hasAssetsState) {
      const statusResult = assetsSessionId
        ? await getStoredAssetsServerStatus(assetsSessionId)
        : { kind: 'unusable', status: null };

      if (storedAssetsRecord && (!currentUploadVideoId || storedAssetsRecord.videoId === currentUploadVideoId)) {
        uploadedVideoId = storedAssetsRecord.videoId;
        currentUploadBackend = 'assets';
        currentAssetsRecord = storedAssetsRecord;
        localStorage.setItem('currentUploadVideoId', storedAssetsRecord.videoId);
        localStorage.setItem('currentUploadFileName', storedAssetsRecord.file.name);
        localStorage.setItem('currentUploadBackend', 'assets');
        localStorage.setItem('currentUploadAssetsUploadId', storedAssetsRecord.uploadId);
        localStorage.setItem('currentUploadAssetsVideoId', storedAssetsRecord.assetsVideoId);
        localStorage.setItem('currentUploadSessionId', storedAssetsRecord.sessionId);
        fileName.textContent = storedAssetsRecord.file.name;
        fileSize.textContent = formatFileSize(storedAssetsRecord.file.size);

        if (statusResult.kind === 'ok') {
          try {
            const serverProgress = reconcileAssetsServerStatus(storedAssetsRecord, statusResult.status);
            currentAssetsRecord = saveUploadRecord(localStorage, {
              ...storedAssetsRecord,
              state: serverProgress.complete ? 'chunks-complete' : 'paused',
              confirmedCount: serverProgress.confirmedCount,
              lastErrorCode: null,
              updatedAt: Date.now(),
            });
            localStorage.setItem('currentUploadCompleted', String(serverProgress.complete));

            if (serverProgress.complete) {
              videoUploadComplete = true;
              fileInfo.style.display = 'block';
              uploadZone.style.display = 'none';
              setConfirmedProgress(serverProgress.confirmedCount, serverProgress.totalChunks, true);
              submitBtn.disabled = false;
              submitBtn.innerHTML = '📤 Send inn film';
              await showModal(
                'Ambætarin hevur váttað allar fílupartarnar. Kanna skrásetingina og send inn.',
                'info',
                'Til reiðar at senda inn',
              );
            } else {
              videoUploadComplete = false;
              resetSelectedFileUi();
              setConfirmedProgress(serverProgress.confirmedCount, serverProgress.totalChunks, false);
              progressFill.style.background = 'var(--color-warning)';
              progressText.textContent = `⏸ ${serverProgress.confirmedCount}/${serverProgress.totalChunks} partar eru váttaðir. Vel somu fíluna fyri at halda fram.`;
              submitBtn.disabled = true;
              submitBtn.innerHTML = '⏸ Vel somu fíluna og halt fram';
              await showModal(
                'Ein avbrotin uppløga er funnin. Vel somu fíluna fyri at halda fram.',
                'info',
                'Uppløga funnin',
              );
            }
            return;
          } catch (error) {
            console.warn('Saved Assets upload is no longer usable:', error);
          }
        } else if (
          statusResult.kind === 'unavailable' &&
          storedAssetsRecord.lastErrorCode !== 'UPLOAD_SESSION_EXPIRED'
        ) {
          videoUploadComplete = false;
          resetSelectedFileUi();
          setConfirmedProgress(storedAssetsRecord.confirmedCount, storedAssetsRecord.totalChunks, false);
          progressFill.style.background = 'var(--color-warning)';
          progressText.textContent = 'Støðan kundi ikki váttast. Vel somu fíluna fyri at royna aftur.';
          submitBtn.disabled = true;
          submitBtn.innerHTML = '⏸ Vel somu fíluna og halt fram';
          await showModal(
            'Ambætarin svarar ikki beint nú. Vel somu fíluna fyri at halda fram, tá sambandið er aftur.',
            'warning',
            'Støðan ikki váttað',
          );
          return;
        }

        clearAssetsUploadSessionState(storedAssetsRecord);
        showFreshAssetsUploadUi();
        await showModal(
          'Fyrra uppløgan kann ikki halda fram. Vel videofíluna fyri at byrja av nýggjum.',
          'warning',
          'Byrja av nýggjum',
        );
        return;
      }

      if (!storedAssetsRecord && statusResult.kind === 'ok' && currentUploadVideoId) {
        try {
          const status = statusResult.status;
          const expectedAssetsVideoId = localStorage.getItem('currentUploadAssetsVideoId');
          if (expectedAssetsVideoId && status.videoId !== undefined && status.videoId !== expectedAssetsVideoId) {
            throw new UploadError('UPLOAD_STATUS_INVALID');
          }
          const serverProgress = normalizeUploadStatus(status, status.totalChunks);
          if (serverProgress.complete) {
            uploadedVideoId = currentUploadVideoId;
            currentUploadBackend = 'assets';
            videoUploadComplete = true;
            localStorage.setItem('currentUploadBackend', 'assets');
            localStorage.setItem('currentUploadCompleted', 'true');
            if (status.videoId) localStorage.setItem('currentUploadAssetsVideoId', status.videoId);
            fileName.textContent = currentUploadFileName || 'Videofíla';
            fileSize.textContent = '';
            fileInfo.style.display = 'block';
            uploadZone.style.display = 'none';
            setConfirmedProgress(serverProgress.confirmedCount, serverProgress.totalChunks, true);
            submitBtn.disabled = false;
            submitBtn.innerHTML = '📤 Send inn film';
            await showModal(
              'Ambætarin hevur váttað allar fílupartarnar. Kanna skrásetingina og send inn.',
              'info',
              'Til reiðar at senda inn',
            );
            return;
          }
        } catch (error) {
          console.warn('Older Assets upload state is not usable:', error);
        }
      }

      clearAssetsUploadSessionState(storedAssetsRecord);
      showFreshAssetsUploadUi();
      await showModal(
        'Gamla uppløgustøðan kann ikki brúkast. Vel videofíluna fyri at byrja eina nýggja uppløgu.',
        'warning',
        'Byrja av nýggjum',
      );
      return;
    }

    if (currentUploadVideoId && currentUploadFileName && storedBackend === 'legacy' && storedUploadComplete) {
      // A completed direct-Azure upload can still use the established flow.
      uploadedVideoId = currentUploadVideoId;
      currentUploadBackend = 'legacy';
      videoUploadComplete = true;
      submitBtn.disabled = false;
      submitBtn.innerHTML = '📤 Send inn film';
      progressContainer.style.display = 'block';
      progressFill.style.width = '100%';
      progressFill.style.background = 'var(--color-success)';
      progressText.textContent = '✅ Uppløgan er longu fullførd. Fyll út skrásetingina og send inn.';
      await showModal(
        'Fyrra uppløgan og skrásetingin eru funnar fram aftur. Kanna upplýsingarnar og send inn.',
        'info',
        'Uppløgan er funnin',
      );
      return;
    }

    if (currentUploadVideoId && currentUploadFileName) {
      uploadedVideoId = null;
      currentUploadBackend = storedBackend;
      videoUploadComplete = false;
      resetSelectedFileUi();
      progressContainer.style.display = 'block';
      progressFill.style.width = '0%';
      progressFill.style.background = 'var(--color-warning)';
      progressText.textContent = 'Støðan kundi ikki váttast. Vel videofíluna aftur.';
      submitBtn.disabled = true;
      submitBtn.innerHTML = '📤 Vel videofílu';
      await showModal(
        'Ein eldri uppløga varð funnin, men støðan kundi ikki váttast. Vel videofíluna aftur.',
        'warning',
        'Uppløgan má váttast',
      );
      return;
    }

    if (hasFormData) {
      const restore = await showConfirm(
        '📋 Goymd skráseting funnin!\n\n' + 'Vilt tú brúka goymdu skrásetingina?',
        'Goymd skráseting',
      );

      if (!restore) {
        localStorage.removeItem('uploadFormData');
        // Reload to clear the form
        location.reload();
      }
    }
  })().catch((error) => {
    console.error('Failed to recover the saved upload state:', error);
  });

  // Prevent navigation during upload
  const handleBeforeUnload = (e) => {
    if (isUploading) {
      e.preventDefault();
      e.returnValue =
        'Uppløga í gongd. Um tú ferð frá síðuni, verður uppløgan sett í bíðistøðu, og tú kanst halda fram seinni.';
      return e.returnValue;
    }
  };

  window.addEventListener('beforeunload', handleBeforeUnload);

  // Form submission - only update metadata and trigger encoding (video already uploaded)
  form.addEventListener('submit', async (e) => {
    e.preventDefault();

    // Check if video was uploaded
    if (!uploadedVideoId) {
      await showModal('Vel fyrst eina videofílu. Uppløgan byrjar sjálvvirknandi.', 'warning', 'Eingin fíla vald');
      return;
    }

    // Check if upload is still in progress
    if (isUploading) {
      await showModal('Bíða eftir at uppløgan verður liðug, áðrenn tú sendur inn.', 'warning', 'Uppløga í gongd');
      return;
    }

    if (!videoUploadComplete) {
      await showModal(
        'Ambætarin hevur ikki váttað allar fílupartarnar enn. Vel somu videofílu fyri at halda fram við uppløguni.',
        'warning',
        'Uppløgan er ikki liðug',
      );
      return;
    }

    if (!(await ensureRightsTermsAccepted())) {
      return;
    }

    submitBtn.disabled = true;
    submitBtn.innerHTML = 'Sendur inn...';
    progressText.textContent = 'Goymir upplýsingar...';

    try {
      // Collect all form data
      const pricingType = document.getElementById('pricingType').value;
      const pricingTier = document.getElementById('pricingTier').value;
      const customPrice = parseFloat(document.getElementById('customPrice').value) || null;

      // Calculate rental price based on pricing type
      let rentalPrice = 0;
      if (pricingType === 'free') {
        rentalPrice = 0;
      } else if (pricingType === 'custom' && customPrice !== null) {
        rentalPrice = Math.round(customPrice * 100); // Convert DKK to cents/øre
      } else if (pricingType === 'standard' && pricingTier) {
        // Map pricing tier to price in cents/øre
        const tierPrices = {
          short_under_3mo: 2900, // DKK 29
          short_under_1yr: 1900, // DKK 19
          short_under_4yr: 700, // DKK 7
          short_over_4yr: 0,
          feature_under_2yr: 5900, // DKK 59
          feature_over_2yr: 3900, // DKK 39
          feature_over_4yr: 0,
          doc_short_under_1yr: 1900, // DKK 19
          doc_short_under_4yr: 700, // DKK 7
          doc_short_over_4yr: 0,
          doc_long_under_2yr: 5900, // DKK 59
          doc_long_under_4yr: 3900, // DKK 39
          doc_long_over_4yr: 0,
          tv_series: 2900, // DKK 29 per episode
        };
        rentalPrice = tierPrices[pricingTier] || 0;
      }

      const metadata = {
        category: document.getElementById('category').value,
        originalTitle: document.getElementById('originalTitle').value,
        internationalTitle: document.getElementById('internationalTitle').value,
        loglineFo: document.getElementById('loglineFo').value,
        loglineEn: document.getElementById('loglineEn').value,
        synopsisFo: document.getElementById('synopsisFo').value,
        synopsisEn: document.getElementById('synopsisEn').value,
        posterUrl: document.getElementById('posterUrl').value,
        stillUrl: document.getElementById('stillUrl').value,
        encode4K: document.getElementById('encode4K').checked,
        ...getRightsTermsPayload(),

        // Pricing
        pricingType: pricingType,
        pricingTier: pricingTier || null,
        customPrice: customPrice ? Math.round(customPrice * 100) : null,
        rentalPrice: rentalPrice,
        rentalDurationHours:
          pricingType === 'free' ? null : parseInt(document.getElementById('rentalDuration').value, 10) || 48,

        // Bank information (only if not free)
        bankName: pricingType === 'free' ? null : document.getElementById('bankName').value,
        bankRegistrationNo: pricingType === 'free' ? null : document.getElementById('bankRegistrationNo').value,
        bankAccountNo: pricingType === 'free' ? null : document.getElementById('bankAccountNo').value,

        // Crew
        director: getRoleArray('director'),
        screenwriter: getRoleArray('screenwriter'),
        producer: getRoleArray('producer'),
        dop: getRoleArray('dop'),
        editor: getRoleArray('editor'),
        composer: getRoleArray('composer'),

        // Cast and Crew arrays
        mainCast: getCastArray(),
        keyCrew: getCrewArray(),
        screenings: getScreeningsArray(),

        // Companies
        productionCompany: document.getElementById('productionCompany').value,
        coproductionCompany: document.getElementById('coproductionCompany').value,

        // Technical
        runtimeHours: parseInt(document.getElementById('runtimeHours').value, 10) || 0,
        runtimeMinutes: parseInt(document.getElementById('runtimeMinutes').value, 10) || 0,
        language: document.getElementById('language').value,
        subtitles: document.getElementById('subtitles').value,
        productionYear: parseInt(document.getElementById('productionYear').value, 10) || new Date().getFullYear(),
        imdbLink: document.getElementById('imdbLink').value,
        geoRestricted: document.getElementById('geoRestricted').checked,
        ageRating: document.getElementById('ageRating').value || null,
      };

      // Include Assets info if this was an Assets upload
      const assetsVideoId = localStorage.getItem('currentUploadAssetsVideoId');
      const assetsSessionId = localStorage.getItem('currentUploadSessionId');
      if (assetsVideoId) metadata.assetsVideoId = assetsVideoId;
      if (assetsSessionId) metadata.assetsSessionId = assetsSessionId;
      metadata.state = 'uploaded';

      // Update video metadata (video file already uploaded)
      try {
        await api.updateVideo(uploadedVideoId, metadata);
        console.log(`✅ Metadata updated for videoId: ${uploadedVideoId}`);
      } catch (metadataError) {
        console.error('Failed to update metadata:', metadataError);
        await showModal(
          'Tað bar ikki til at goyma upplýsingarnar beint nú. Uppløgan er varðveitt, so tú kanst royna aftur.',
          'error',
          'Brek',
        );
        submitBtn.disabled = false;
        submitBtn.innerHTML = '📤 Send inn film';
        return;
      }

      // Upload images if provided
      try {
        const imageUpdates = {};

        // Upload poster (file takes priority over URL)
        if (selectedPosterFile) {
          progressText.textContent = 'Leggur upp plakat...';
          const posterResult = await api.uploadImage(selectedPosterFile, 'poster', uploadedVideoId);
          imageUpdates.posterUrl = posterResult.url;
          if (posterResult.imageId) imageUpdates.posterAssetId = posterResult.imageId;
        }

        // Upload still (file takes priority over URL)
        if (selectedStillFile) {
          progressText.textContent = 'Leggur upp stillmynd...';
          const stillResult = await api.uploadImage(selectedStillFile, 'still', uploadedVideoId);
          imageUpdates.stillUrl = stillResult.url;
          if (stillResult.imageId) imageUpdates.stillAssetId = stillResult.imageId;
        }

        // Persist image URLs to database
        if (Object.keys(imageUpdates).length > 0) {
          await api.updateVideo(uploadedVideoId, imageUpdates);
        }
      } catch (imageError) {
        console.error('Image upload error:', imageError);
        // Don't fail the whole submission if images fail
        await showModal(
          'Upplýsingarnar eru goymdar, men tað bar ikki til at leggja myndina upp beint nú.',
          'warning',
          'Partvíst eydnað',
        );
      }

      // Upload subtitles if provided
      if (subtitlesToUpload.length > 0) {
        try {
          for (let i = 0; i < subtitlesToUpload.length; i++) {
            const subtitle = subtitlesToUpload[i];
            progressText.textContent = `Leggur upp undirtekst ${i + 1}/${subtitlesToUpload.length} (${subtitle.label})...`;
            await api.uploadSubtitle(uploadedVideoId, subtitle.file, subtitle.language, subtitle.label);
          }
        } catch (subtitleError) {
          console.error('Subtitle upload error:', subtitleError);
          // Don't fail the whole submission if subtitles fail
          await showModal(
            'Upplýsingarnar eru goymdar, men tað bar ikki til at leggja undirtekstirnar upp beint nú.',
            'warning',
            'Partvíst eydnað',
          );
        }
      }

      // Finalize and trigger encoding
      try {
        progressText.textContent = 'Byrjar enkoding...';
        const assetsSessionId = localStorage.getItem('currentUploadSessionId');
        if (currentAssetsRecord) {
          currentAssetsRecord = saveUploadRecord(localStorage, {
            ...currentAssetsRecord,
            state: 'finalizing',
            updatedAt: Date.now(),
          });
        }

        await finalizeCurrentVideo(assetsSessionId);
        console.log(`✅ Encoding triggered for videoId: ${uploadedVideoId}`);
      } catch (encodingError) {
        console.error('Failed to trigger encoding:', encodingError);
        if (currentAssetsRecord) {
          currentAssetsRecord = saveUploadRecord(localStorage, {
            ...currentAssetsRecord,
            state: 'chunks-complete',
            updatedAt: Date.now(),
          });
        }
        await showModal(
          'Filmurin og upplýsingarnar eru goymd, men tað bar ikki til at byrja enkodingina beint nú. Uppløgan er varðveitt; trýst á “Send inn film” fyri at royna aftur.\n\n' +
            safeUploadMessage(encodingError),
          'warning',
          'Enkoding miseydnaðist',
        );
        submitBtn.disabled = false;
        submitBtn.innerHTML = '📤 Send inn film';
        return;
      }

      // Only discard resumable state after Assets finalization succeeded.
      clearUploadStateAfterSuccessfulFinalize();

      // Show success message
      progressText.textContent = '✅ Filmurin er sendur inn!';
      progressFill.style.width = '100%';
      submitBtn.innerHTML = '✅ Sent inn';

      // Show success modal and navigate
      await showModal(
        `✅ ${t('upload_success')}\n\nFilmurin er sendur inn og enkoding byrjar skjótt.`,
        'success',
        'Filmurin er sendur inn',
      );

      router.navigate('/manage');
    } catch (error) {
      // Catch-all for unexpected errors
      console.error('Unexpected submission error:', error);
      await showModal('Eitt óvæntað brek kom fyri. Uppløgan er varðveitt, so tú kanst royna aftur.', 'error', 'Brek');
      submitBtn.disabled = false;
      submitBtn.innerHTML = '📤 Send inn film';
    }
  });

  function getNameInput(prefix) {
    const firstName = document.getElementById(`${prefix}FirstName`)?.value || '';
    const middleName = document.getElementById(`${prefix}MiddleName`)?.value || '';
    const lastName = document.getElementById(`${prefix}LastName`)?.value || '';

    if (!firstName && !middleName && !lastName) return null;

    return { firstName, middleName, lastName };
  }

  function getRoleArray(role) {
    const count = roleCounts[role] || 1;
    const people = [];
    for (let i = 0; i < count; i++) {
      const member = getNameInput(`${role}${i}`);
      if (member) people.push(member);
    }
    return people.length > 0 ? people : null;
  }

  function getCastArray() {
    const cast = [];
    for (let i = 0; i < castCount; i++) {
      const member = getNameInput(`cast${i}`);
      if (member) cast.push(member);
    }
    return cast.length > 0 ? cast : null;
  }

  function getCrewArray() {
    const crew = [];
    for (let i = 0; i < crewCount; i++) {
      const member = getNameInput(`crew${i}`);
      const role = document.getElementById(`crew${i}Role`)?.value || '';
      if (member || role) {
        crew.push({
          ...member,
          role: role || null,
        });
      }
    }
    return crew.length > 0 ? crew : null;
  }

  // Check for incomplete uploads on page load (resumable upload feature)
  setTimeout(async () => {
    const incompleteUploads = api.getAllIncompleteUploads();

    if (incompleteUploads.length > 0) {
      // Sort by most recent first
      incompleteUploads.sort((a, b) => b.lastUpdated - a.lastUpdated);
      const mostRecent = incompleteUploads[0];

      const percentage = Math.round((mostRecent.uploadedChunks.length / mostRecent.totalChunks) * 100);
      const fileSizeMB = (mostRecent.fileSize / (1024 * 1024)).toFixed(2);
      const timeAgo = formatTimeAgo(mostRecent.lastUpdated);

      const message =
        `📤 Óliðug uppløga funnin!\n\n` +
        `Fíla: ${mostRecent.fileName}\n` +
        `Stødd: ${fileSizeMB} MB\n` +
        `Framgongd: ${percentage}%\n` +
        `Síðst dagført: ${timeAgo}\n\n` +
        `Vilt tú halda fram við hesa uppløgu?\n\n` +
        `Viðmerking: Tú mást velja somu fílu umaftur fyri at halda fram.`;

      const resume = await showModal(message, 'warning', 'Halda fram við uppløgu?');

      // For now, just clear the old progress
      // TODO: Implement actual resume functionality by pre-filling the form
      if (!resume) {
        api.clearUploadProgress(mostRecent.videoId);
        await showModal('Fyrra uppløgan er strikað. Tú kanst byrja eina nýggja uppløgu.', 'info', 'Strikað');
      } else {
        await showModal(
          `Fyri at halda fram:\n\n` +
            `1. Vel somu fílu: "${mostRecent.fileName}"\n` +
            `2. Fyll út skrásetingina\n` +
            `3. Trýst á "Legg upp film"\n\n` +
            `Uppløgan heldur sjálvvirknandi fram frá ${percentage}%.`,
          'info',
          'Hvussu halda fram',
        );
      }
    }
  }, 500); // Small delay to let page render first
}

function formatTimeAgo(timestamp) {
  const now = Date.now();
  const diffMs = now - timestamp;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffMins > 0) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  return 'Just now';
}
